/**
 * ログイン画面。
 *
 * 新規登録リンクは**意図的に無い**: 管理者アカウントは Firebase コンソールで
 * 手動作成し、`admins/{uid}` を1件足す運用(docs/10 §5 の MVP)。
 */
import { useState, type FormEvent } from 'react';
import { AdminAuthError, type AdminAuthService } from '../services/adminAuth';

interface Props {
  readonly auth: AdminAuthService;
  readonly initialError: AdminAuthError | null;
  readonly projectId: string;
  readonly usingEmulator: boolean;
}

export function LoginScreen({ auth, initialError, projectId, usingEmulator }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(initialError?.message ?? null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await auth.signIn(email, password);
    } catch (cause) {
      // 原因(SDK のエラー)はコンソールに残す。画面に出すのは翻訳済みの一言だけ。
      console.error('[admin] sign-in failed', cause);
      setError(cause instanceof AdminAuthError ? cause.message : 'ログインに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form
        className="login__card"
        onSubmit={(event) => {
          void onSubmit(event);
        }}
      >
        <h1 className="login__title">PaLTown 管理画面</h1>
        <p className="login__sub">管理者アカウントでログインしてください。</p>

        {error !== null && (
          <div className="notice notice--error login__error" role="alert">
            {error}
          </div>
        )}

        <label className="field">
          <span className="field__label">メールアドレス</span>
          <input
            className="input"
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            required
          />
        </label>

        <label className="field">
          <span className="field__label">パスワード</span>
          <input
            className="input"
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
            required
          />
        </label>

        <button className="button button--primary" type="submit" disabled={busy}>
          {busy ? 'ログイン中…' : 'ログイン'}
        </button>

        <p className="login__foot">
          プロジェクト: <span className="mono">{projectId}</span>
          {usingEmulator && ' (エミュレータ)'}
          <br />
          アカウントの発行は Firebase コンソールから行います。
        </p>
      </form>
    </div>
  );
}
